// fastTrackUpsell.js (mixin)
// Priority Physician Review — the post-purchase offer, wired into a checkout.
//
// Every checkout in this app makes the same offer the same way: the order is
// captured, the server says success, and before the page leaves for its
// thank-you route the patient is asked once whether they want priority review.
// The modal, the charge and the guards live in FastTrackUpsellModal.vue; this
// mixin is only the three lines of glue each checkout would otherwise repeat.
//
// A checkout uses it like this:
//
//   mixins: [fastTrackUpsell],
//   // optional — only where the checkout already fires its own `purchase`
//   fastTrackFunnelStep: 18,
//   ...
//   // in processPayment(), replacing `this.pushToRoute(a, t)`:
//   this.offerFastTrackThen(context, () => this.pushToRoute(a, t))
//
// and renders `<fast-track-upsell-modal>` guarded on `fastTrackOffer` (see any
// checkout for the exact markup).
//
// If the add-on is missing, inactive, or the catalog is unreachable, the
// continuation runs immediately and the patient never sees anything. The offer
// can never delay or block a confirmed order.

import FastTrackUpsellModal from '@/components/checkouts/FastTrackUpsellModal.vue'
import {
    loadFastTrackAddon,
    buildFastTrackPurchaseParams,
    stashFastTrackContext
} from '@/utils/fastTrackUpsell.js'

// The order is already paid whenever either of these runs, so neither the
// catalog nor the modal is ever allowed to hold the redirect for longer than
// this. Whatever is slow, the patient still reaches their receipt.
const CATALOG_WAIT_MS = 4000
const RENDER_WAIT_MS = 4000

export default {
    components: {
        FastTrackUpsellModal
    },
    data() {
        return {
            // { product, plan } once resolved; null means make no offer at all.
            fastTrackAddon: null,
            // The charge context while the modal is up; null the rest of the time.
            fastTrackOffer: null,
            // Where the checkout was headed. Held until the offer is answered.
            fastTrackNext: null,
            // One offer per order — a second success can never re-ask.
            fastTrackAnswered: false
        }
    },
    created() {
        // Cleared when the modal reports itself on screen. Not reactive — nothing
        // renders off it.
        this.fastTrackWatchdog = null
        // Started up front, off the shared product cache, so the answer is normally
        // ready long before an order succeeds. The promise is KEPT rather than only
        // its result: an order placed before the catalog answers would otherwise
        // read a still-null fastTrackAddon and skip the offer, which is exactly how
        // this appears on one checkout and not the next. offerFastTrackThen awaits
        // this instead. Not reactive — the render reads fastTrackAddon.
        this.fastTrackReady = loadFastTrackAddon().then((resolved) => {
            this.fastTrackAddon = resolved
            return resolved
        })
    },
    beforeDestroy() {
        if (this.fastTrackWatchdog) clearTimeout(this.fastTrackWatchdog)
    },
    methods: {
        /**
         * Make the offer, then continue.
         *
         * context — { userId, paymentMethod, paymentTerms, firstName, lastName,
         *             email, shippingAddress, billingAddress,
         *             trackingSessionId, ipAddress }
         * next    — what the checkout was about to do (its pushToRoute call).
         */
        async offerFastTrackThen(context, next) {
            if (this.fastTrackAnswered) {
                next()
                return
            }
            // The catalog load starts at created() and is normally done minutes
            // before this. A checkout that pays fast can still beat it, so wait for
            // the answer rather than reading a property that may not be set yet.
            // loadFastTrackAddon never rejects — it answers null instead.
            //
            // Bounded, because this wait sits between a captured payment and the
            // receipt: a catalog that never answers must cost the offer, never the
            // confirmation.
            if (!this.fastTrackAddon && this.fastTrackReady) {
                await Promise.race([
                    this.fastTrackReady,
                    new Promise((resolve) => setTimeout(resolve, CATALOG_WAIT_MS))
                ])
                if (!this.fastTrackAddon) {
                    console.error('[fast-track] catalog did not answer in time — no offer made')
                }
            }
            if (!this.fastTrackAddon) {
                console.warn('[fast-track] no offer to make — continuing to the receipt')
                next()
                return
            }
            this.fastTrackNext = next
            this.fastTrackOffer = context

            // The order is PAID by the time this runs. The redirect is therefore not
            // the offer's to hold: if the modal does not report itself on screen,
            // this leaves anyway. Without it, any render failure — a Vue error, a
            // component that never resolves, an exception in a sibling — strands a
            // charged customer on the checkout's loading overlay with no way out.
            if (this.fastTrackWatchdog) clearTimeout(this.fastTrackWatchdog)
            this.fastTrackWatchdog = setTimeout(() => {
                console.error('[fast-track] offer never appeared — leaving for the receipt without it')
                this.onFastTrackDone()
            }, RENDER_WAIT_MS)
        },
        /** The modal mounted and is visible — the watchdog can stand down. */
        onFastTrackShown() {
            if (this.fastTrackWatchdog) {
                clearTimeout(this.fastTrackWatchdog)
                this.fastTrackWatchdog = null
            }
        },
        /**
         * Klarna only. Hand the same context to KlarnaReturn.vue before the
         * redirect, since that page reloads with none of this in memory.
         * Call it with the payment intent id, right where the cart snapshot
         * is written.
         */
        stashFastTrackOffer(paymentIntentId, context) {
            // Deliberately NOT gated on fastTrackAddon. This runs inline in
            // payWithKlarna, moments before Stripe redirects off-site, and can
            // easily beat the catalog load — gating here would silently drop the
            // Klarna offer whenever the patient paid quickly. Whether an offer is
            // made is decided on the return page, which resolves the add-on itself;
            // a stash it has no use for is read once and thrown away.
            // The return page is shared by every checkout, so it can't know whose
            // step numbering to use — the checkout that started the payment sends
            // its own along. Absent where the checkout tracks no purchase.
            stashFastTrackContext(paymentIntentId, {
                ...context,
                funnelStep: this.$options.fastTrackFunnelStep,
                form: this.$options.fastTrackForm
            })
        },
        /** Answered — bought or declined. Either way the checkout carries on. */
        onFastTrackDone() {
            if (this.fastTrackWatchdog) {
                clearTimeout(this.fastTrackWatchdog)
                this.fastTrackWatchdog = null
            }
            const next = this.fastTrackNext
            this.fastTrackAnswered = true
            this.fastTrackOffer = null
            this.fastTrackNext = null
            // Guarded: the watchdog and a real answer can both arrive, and running
            // the checkout's redirect twice would push two routes.
            if (next) next()
        },
        /**
         * The add-on charge succeeded. It gets its own `purchase` rather than being
         * folded into the order's: that one already fired for the medication alone,
         * and adding this revenue to it would mean a second event wearing the same
         * ids. Only fired where the checkout declares its own step number — a
         * checkout that tracks no purchase of its own has no numbering to extend.
         */
        onFastTrackPurchased(addon, result) {
            // KlarnaReturn.vue is shared, so its step number rides in with the
            // stashed context; a checkout offering inline uses its own.
            const funnelStep =
                (this.fastTrackOffer && this.fastTrackOffer.funnelStep) ||
                this.$options.fastTrackFunnelStep
            const form =
                (this.fastTrackOffer && this.fastTrackOffer.form) ||
                this.$options.fastTrackForm
            if (!funnelStep || !form || !window.tracking) return
            try {
                window.tracking.event('purchase', buildFastTrackPurchaseParams(addon, result, funnelStep, form))
            } catch (error) {
                console.warn('[fast-track] purchase event failed', error)
            }
        }
    }
}
